"use client"

import * as React from "react"
import { ChevronsUpDown } from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/ui/components/ui/sidebar"
import { Organization } from "@/machines/types/organization.ts";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";

export function TeamSwitcher({
                               organizations,
                             }: {
  organizations: Organization[]
}) {
  const { isMobile } = useSidebar()
  const { t } = useTranslation("ui")
  const { selectedOrganizationId, setSelectedOrganizationId } = useOrganizationStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const first = organizations?.[0];
  const selected = organizations.find(o => o.id === selectedOrganizationId) || first;
  const [activeTeam, setActiveTeam] = React.useState<Organization | undefined>(selected)

  // Ensure default selection on load if none
  React.useEffect(() => {
    if (!selectedOrganizationId && first) {
      setSelectedOrganizationId(first.id);
      setActiveTeam(first);
    }
  }, [selectedOrganizationId, first, setSelectedOrganizationId])

  // Keep local active in sync when store changes or org list updates
  React.useEffect(() => {
    const next = organizations.find(o => o.id === selectedOrganizationId) || first;
    setActiveTeam(next);
  }, [selectedOrganizationId, organizations, first])

  if (!activeTeam) {
    return null
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              {/* Initials avatar — the only thing visible when the rail is
                  collapsed (the name + chevron get clipped at size-8). */}
              <div className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground text-xs font-semibold">
                {activeTeam.name.slice(0, 2).toUpperCase()}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {activeTeam.name}
                </span>
                <span className="truncate text-xs">{t("teamSwitcher.subtitle")}</span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("teamSwitcher.groupLabel")}
            </DropdownMenuLabel>
            {organizations.map((organization, index) => (
              <DropdownMenuItem
                key={organization.id}
                onClick={async () => {
                  setActiveTeam(organization)
                  setSelectedOrganizationId(organization.id)
                  
                  // Invalidate all queries to refresh data with new organization
                  await queryClient.invalidateQueries()
                  
                  // Navigate to locations page with replace
                  navigate("/", { replace: true })

                }}
                className="gap-2 p-2"
              >
                {organization.name}
                <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />

          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
